import React, {
  CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SignalActionsContext } from "@shared/features/signals/common/SignalActions.context";
import SignalsTable from "features/signals/SignalsTable";
import FiltersColumn from "common/Filters/FiltersColumn/FiltersColumn";
import { BASE_BOTS_SIGNALS_PATH, DASHBOARD_PATH } from "common/constants";
import styled, { useTheme } from "styled-components";
import NewLookSignalsList from "features/signals/NewLook/NewLookSignalsList.tsx";
import Card from "common/Boxes/Card";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { SmartTableLayout } from "common/Layouts/SmartTableLayout";
import { useSelector } from "react-redux";
import ConnectExternalAccountModal from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/ConnectExternalAccountModal";
import { useHistory, useRouteMatch } from "react-router-dom";
import { FilterType } from "@shared/types";
import { signalsSelectors } from "@shared/slices/signals.slice";
import useConnectTelegram from "features/onboarding/methodSettings/signalsGroup/connectExternalAccount/useConnectTelegram";
import { sharedLib } from "@shared/services/shared.service";
import { useBottomNavigator } from "utils/hooks/misc";
import SignalsCardsList from "features/signals/SignalsCards/SignalsCardsList";
import { SignalsStatus } from "@shared/features/signals/common/types.ts";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import { MobileBotsTab } from "@shared/features/bots/types.ts";
import { HEADER_HEIGHT } from "common/Home/General/constants.tsx";
import {
  SIGNALS_PAGE_PADDING_BOTTOM,
  SIGNALS_PAGE_TABS_ROW_HEIGHT,
} from "features/signals/common/constants.ts";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import { SignalsPageContext } from "@shared/features/signals/common/SignalsPage.context";
import { getActiveGroups } from "@shared/features/settings/utils.ts";

interface SignalsProps {
  isOpen: boolean;
  isMobileFilters: boolean;
  renderTopContainer?: () => React.JSX.Element;
}

const Signals = ({
  isOpen,
  isMobileFilters,
  renderTopContainer,
}: SignalsProps) => {
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const history = useHistory();
  const { analytics } = useContext(AnalyticsContext);
  const { queryCode, queryUuid } = useConnectTelegram();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { setTabCount } = useContext(PageTabsContext) || {};
  const isBotsPage = !!useRouteMatch(BASE_BOTS_SIGNALS_PATH);
  const { isBulkMode } = useContext(SignalActionsContext)!;
  const { distanceTop } = useContext(PageWrapperContext);
  const { isNewLook } = useTheme();

  const {
    isAdminSignals,
    subGroupId,
    signalsData,
    args,
    getSignalsDataQuery,
    filtersSlice,
    isSignalsFetching,
    isSignalsLoading,
    setTabToCount,
    showEmpty,
    getGroupsDataStatus,
    getAdminSubGroupsStatus,
  } = useContext(SignalsPageContext)!;

  const [showConnectExternalAccountModal, setShowConnectExternalAccountModal] =
    useState(
      !user?.hasExternalAccount() &&
        !user?.has_groups &&
        !user?.isAssetManager() &&
        !user?.isWhopExperience() &&
        !user?.is_sub_user
    );

  useEffect(() => {
    if (subGroupId) {
      const analyticsArgs = {
        groupChannelId: subGroupId,
      };
      if (isAdminSignals) {
        if (isOpen) analytics?.adminPanelOpenSignalsView(analyticsArgs);
        else analytics?.adminPanelClosedSignalsView(analyticsArgs);
      } else {
        if (isOpen) analytics?.openSignalsView(analyticsArgs);
        else analytics?.closedSignalsView(analyticsArgs);
      }
    }
  }, [isOpen, analytics, isAdminSignals]);

  useEffect(() => {
    setTabToCount((tabToCount) => {
      return {
        ...tabToCount,
        [isOpen ? SignalsStatus.OPEN : SignalsStatus.CLOSED]:
          isSignalsLoading || !getSignalsDataQuery.currentData
            ? undefined
            : signalsData?.length,
      };
    });
    const tabName = isBotsPage
      ? MobileBotsTab.SIGNALS
      : isOpen
      ? SignalsStatus.OPEN
      : SignalsStatus.CLOSED;
    setTabCount?.(tabName, isSignalsLoading ? undefined : signalsData?.length);
  }, [isSignalsLoading, signalsData, isOpen, getSignalsDataQuery.currentData]);

  const loadingStatesData = useMemo(() => {
    return {
      loading: isSignalsFetching,
    };
  }, [isSignalsFetching]);

  const filters = useMemo(() => {
    return {
      loadingStatesSelector: () => loadingStatesData,
      selectors: filtersSlice.selectors,
      actions: filtersSlice.actions,
      hideFilterTypes: isMobileFilters ? [FilterType.GROUPS] : [],
    };
  }, [filtersSlice, loadingStatesData, isMobileFilters]);

  const onConnectExternalAccountSuccess = () => {
    getGroupsDataStatus.refetch();
    setShowConnectExternalAccountModal(false);
  };

  const emptyStateProps = {
    isOpen,
    filtersSlice,
    hasData: !!getSignalsDataQuery.data?.signals?.length,
    // Admins' count only drives the new look's "no groups" state; classic
    // keeps its per-group empty state.
    numGroups: isAdminSignals
      ? isNewLook
        ? (getAdminSubGroupsStatus.data as unknown[] | undefined)?.length
        : undefined
      : getActiveGroups(getGroupsDataStatus.data)?.length,
    isAdmin: isAdminSignals,
  };

  const onRefresh = () => {
    try {
      getSignalsDataQuery.refetch();
      if (isAdminSignals) {
        getAdminSubGroupsStatus.refetch();
      } else {
        getGroupsDataStatus.refetch();
      }
    } catch {}
  };

  const hideFilterTypes = useMemo(
    () => (isAdminSignals ? [FilterType.GROUPS] : undefined),
    [isAdminSignals]
  );

  if (isBottomNavigator) {
    return (
      <SignalsCardsList
        data={signalsData}
        loading={isSignalsLoading}
        isOpenSignals={isOpen}
        renderTopContainer={renderTopContainer}
        emptyStateProps={emptyStateProps}
        refreshing={isSignalsFetching}
        onRefresh={onRefresh}
      />
    );
  }

  const signalsTable = (
    <SignalsTable
      isOpen={isOpen}
      signalsData={signalsData}
      isLoading={isSignalsFetching}
      precisionSelector={signalsSelectors.selectPrecision(args)}
      showEmpty={
        // New look: with no groups the signals query never runs; show the
        // "connect a group" empty state (as on phones) instead of a blank table.
        showEmpty ||
        (isNewLook &&
          !isSignalsLoading &&
          (isAdminSignals
            ? getAdminSubGroupsStatus.isSuccess
            : getGroupsDataStatus.isSuccess) &&
          !emptyStateProps.numGroups)
      }
      emptyStateProps={emptyStateProps}
    />
  );

  const connectExternalAccountModal = (
    <ConnectExternalAccountModal
      visible={showConnectExternalAccountModal}
      onSuccess={onConnectExternalAccountSuccess}
      onCancel={() => history.push(DASHBOARD_PATH)}
      code={queryCode}
      uuid={queryUuid}
    />
  );

  if (isNewLook) {
    return (
      <>
        <NewLookSignalsList tableComponent={signalsTable} />
        {connectExternalAccountModal}
      </>
    );
  }

  const CONTENT_HEIGHT = `calc(100vh - ${HEADER_HEIGHT}px - ${SIGNALS_PAGE_TABS_ROW_HEIGHT}px - ${SIGNALS_PAGE_PADDING_BOTTOM}px - ${
    isBulkMode ? 48 : 0
  }px - ${distanceTop}px)`;

  return (
    <>
      <Layout>
        {!isMobileFilters && (
          <FiltersColumn
            selectLoadingStates={filters.loadingStatesSelector}
            filtersActions={filters.actions}
            filtersSelectors={filters.selectors}
            hideFilterTypes={hideFilterTypes}
            expanded={true}
            height={CONTENT_HEIGHT}
          />
        )}
        <GridItem $gridArea="table">
          <SmartTableLayout withSearch={true} tableComponent={signalsTable} />
        </GridItem>
      </Layout>
      <ConnectExternalAccountModal
        visible={showConnectExternalAccountModal}
        onSuccess={onConnectExternalAccountSuccess}
        onCancel={() => history.push(DASHBOARD_PATH)}
        code={queryCode}
        uuid={queryUuid}
      />
    </>
  );
};

export default Signals;

const Layout = styled.div<{ style: CSSProperties }>`
  width: 100%;
  display: grid;
  grid-gap: 16px;
  grid-template-columns: 1fr 300px;
  grid-template-areas: "table filters";

  @media (max-width: 1350px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: min-content;
    grid-template-areas: "table";
  }
`;
const GridItem = styled(Card)<{ $gridArea: string }>`
  grid-area: ${(p) => p.$gridArea};
  border-radius: 12px;
`;
